// This file contains some utility functions to parse vendor data

import { replace } from "formik";

// Unused example
const exampleVendorData = {
    "type": "Feature",
    "geometry": {
        "type": "LineString",
        "coordinates": [
            [
                9.09055641,
                55.724054345,
                56.55
            ],
            [
                9.090468116,
                55.724082804,
                -99
            ],
            [
                9.090441688,
                55.724083182,
                -99
            ]
        ]
    },
    "properties": {
        "data_id": "64f0e668-cf5d-11f0-9f98-db84f6d51191",
        "properties": {
            "ogc_fid": "15030",
            "id": "23244",
            "mslink": "23244",
            "ledningsid": "23100",
            "fra_id": "0846S004",
            "til_id": "BI08S091-BI08S092-11,7-02",
            "dobbeltlednnr": "1",
            "systemkode": "1",
            "system": "Spildevand",
            "funktionskode": "1",
            "funktion": "Almindelig ledning",
            "kategoriaflobkode": "4",
            "kategori": "Stikledning",
            "statuskode": "1",
            "status": "I brug/drift",
            "lokalitetid": "469",
            "lokalitetsnavn": "Byzone",
            "materialekode": "5",
            "materiale": "PVC",
            "transportkode": "1",
            "transport": "Gravitation",
            "tvaersnitkode": "1",
            "ledn_form": "Cirkulær",
            "anlag_aar": "2025",
            "dia_indv": "160",
            "dia_handel": "160",
            "ejerfordelingid": "10",
            "ejerkode": "8",
            "ejeforhold": "Billund Spildevand A/S",
            "laengde": "8.05",
            "fra_kote": "56.39",
            "bstmkode_frakote": "5",
            "bstm_frakote": "Landmåling",
            "fys_idx": "0.00",
            "r_idx": "0.00",
            "lerdriftstatuskode": "1",
            "lerdriftstatus": "I drift"
        },
        "translated_properties": {},
        "error_msg": {
            "message": "1 tests have failed",
            "tests": [
                {
                    "attribute": "geometriVertikaleKoordinaterRestriktion",
                    "message": "Geometry cannot have Z-values of -99 (default) if ler:etableringstidspunkt is after 2023-07-01"
                }
            ]
        },
        "config": {
            "transform": {
                "type": [
                    {
                        "Hovedledning": {
                            "ler:id": "ledningsid",
                            "ler:driftsstatus": "lerdriftstatuskode",
                            "ler:ejerskabsforhold": "ejerkode",
                            "ler:udvendigDiameter": "dia_handel|mm",
                            "ler:udvendigMateriale": "materiale",
                            "ler:fareklasse_default": "ikke farlig",
                            "ler:noejagtighedsklasse": "lernoejagtighesdkode",
                            "ler:driftsstatus_default": "i drift",
                            "ler:etableringstidspunkt": "anlag_aar",
                            "ler:ledningstransporttype": "transportkode",
                            "ler:tvaersnitsform_default": "cirkulær",
                            "ler:ejerskabsforhold_default": "ejet af udleverende ledningsejer",
                            "ler:noejagtighedsklasse_default": "&gt; 2.00 m",
                            "ler:etableringstidspunkt_default": "2023"
                        }
                    }
                ],
                "replaceMap": {
                    "ejerkode": {
                        "8": "ejet af udleverende ledningsejer",
                        "_": "ejet af anden ledningsejer"
                    },
                    "transportkode": {
                        "1": "gravitation",
                        "2": "tryk",
                        "3": "vakuum",
                        "_": "gravitation"
                    },
                    "lerdriftstatuskode": {
                        "1": "i drift",
                        "2": "permanent ude af drift",
                        "_": "permanent ude af drift"
                    },
                    "lernoejagtighesdkode": {
                        "1": "&lt;= 0.25 m",
                        "2": "&lt;= 0.50 m",
                        "3": "&lt;= 1.00 m",
                        "4": "&lt;= 2.00 m",
                        "_": "&gt; 2.00 m"
                    }
                },
                "typecolumn": "kategori",
                "elementType": "ler:Afloebsledning",
                "elementParent": "ler:ledningMember"
            },
            "feature": {
                "columns": "*",
                "geom_col": "the_geom",
                "ident_col": "ogc_fid",
                "namespace": "dandas",
                "featuretype": "dandas.gp_delledning_ler"
            },
            "is_manuel": false
        }
    }
};

// Based on the exampleVendorData, lets create som functions to parse the data, and make it easier in our app

// getSourceInformation: returns an object with data_id and featuretype - helping us find the source of the data
export function getSourceInformation(vendorData) {
    if (!vendorData || !vendorData.properties || !vendorData.properties.config || !vendorData.properties.config.feature || !vendorData.properties.properties) {
        return null;
    }

    const featureType = vendorData.properties.config.feature ? vendorData.properties.config.feature.featuretype : null;
    const identCol = vendorData.properties.config.feature ? vendorData.properties.config.feature.ident_col : null;
    const identValue = identCol && vendorData.properties.properties ? vendorData.properties.properties[identCol] : null;

    return {
        featuretype: featureType || null,
        ident: identValue || null
    };
}

// is_manuel: returns true/false if the data is marked as manuel
export function isManualData(vendorData) {
    if (!vendorData || !vendorData.properties || !vendorData.properties.config) {
        return false;
    }

    return vendorData.properties.config.is_manuel === true;
}

// mapTranslations: returns an object containing a mapping of original property names to translated property names, and their values
export function mapTranslations(vendorData) {
    if (!vendorData || !vendorData.properties || !vendorData.properties.properties || !vendorData.properties.config || !vendorData.properties.config.transform) {
        return {};
    }

    const transform = vendorData.properties.config.transform;
    const properties = vendorData.properties.properties;
    const translatedProperties = {};

    // Evaluate the type mapping.
    // if transform.typecolumn exists, we use that to determine which type mapping to use, else we use the first one. If typecolumn is set, but there is only one, we use that
    let typeMapping = null;
    if (transform.typecolumn && Array.isArray(transform.type) && transform.type.length > 0) {
        const typeValue = properties[transform.typecolumn];
        for (const typeObj of transform.type) {
            const key = Object.keys(typeObj)[0];
            if (key === typeValue) {
                typeMapping = typeObj[key];
                break;
            }
        }
    }
    if (!typeMapping && Array.isArray(transform.type) && transform.type.length > 0) {
        typeMapping = Object.values(transform.type[0])[0];
    }

    // Build a list of the mapping
    var mapping = [];

    const getTranslatedInto = (key) => {
        // Check if the key is one of the values in typeMapping
        if (!typeMapping) return null;
        for (const [mappedKey, originalKey] of Object.entries(typeMapping)) {

            // the key may have uom suffix (e.g. "dia_handel|mm"), so we need to check only the part before |
            const uomIndex = originalKey.indexOf('|');
            let checkKey = originalKey;
            if (uomIndex !== -1) {
                checkKey = originalKey.substring(0, uomIndex);
            }
            
            if (checkKey === key) {
                return mappedKey;
            }
        }
        return null;
    }

    const getDefault = (key) => {
        // Get the default value for the key in typeMapping
        if (!typeMapping) return null;
        
        // Check if there is a key with _default suffix
        const defaultKey = key + '_default';
        if (typeMapping.hasOwnProperty(defaultKey)) {
            return typeMapping[defaultKey];
        }
        return null;
    }

    const getReplace = (key) => {
        // Check if the key is in replaceMap
        if (!transform.replaceMap) return false;
        return transform.replaceMap.hasOwnProperty(key);
    }

    const getTranslatedValue = (key, value) => {
        // Check if we have a replace mapping for this key
        if (transform.replaceMap && transform.replaceMap[key]) {
            const replaceOptions = transform.replaceMap[key];
            if (replaceOptions.hasOwnProperty(value)) {
                return replaceOptions[value];
            } else if (replaceOptions.hasOwnProperty('_')) {
                return replaceOptions['_'];
            }
        }

        // Value may have uom (e.g. "dia_handel|mm"), so we need to split on |
        const uomIndex = key.indexOf('|');
        if (uomIndex !== -1) {
            // We have a uom, so add it to the value
            const baseKey = key.substring(0, uomIndex);
            const uom = key.substring(uomIndex + 1);
            return value + ' ' + uom;
        }
        
        // No replace mapping, return original value
        return value;
    }

    // loop though properties and map them according to typeMapping
    for (const [originalKey, originalValue] of Object.entries(properties)) {

        let translatedKey = getTranslatedInto(originalKey);
        let defaultValue = getDefault(translatedKey);
        let replaceOptions = getReplace(originalKey) ? transform.replaceMap[originalKey] : null;
        let translatedValue = translatedKey ? getTranslatedValue(originalKey, originalValue) : null;

        mapping.push({
            defaultValue: defaultValue,
            original: originalKey,
            originalValue:originalValue,
            translateInto: translatedKey,
            translatedValue: translatedValue,
            replaceOptions: replaceOptions,
        });
    }

    // Add entries for ler: values that only have _default definitions (no source mapping)
    if (typeMapping) {
        for (const [mappedKey, originalKey] of Object.entries(typeMapping)) {
            // Check if this is a _default key
            if (mappedKey.endsWith('_default')) {
                // Get the base key without _default suffix
                const baseKey = mappedKey.substring(0, mappedKey.length - 8);
                
                // Check if the base key exists in typeMapping (has a source mapping)
                const hasSourceMapping = Object.values(typeMapping).some(val => {
                    const uomIndex = val.indexOf('|');
                    const checkVal = uomIndex !== -1 ? val.substring(0, uomIndex) : val;
                    return getTranslatedInto(checkVal) === baseKey;
                });
                
                // If no source mapping exists, add it to the mapping array
                if (!hasSourceMapping) {
                    mapping.push({
                        defaultValue: originalKey,
                        original: null,
                        originalValue: null,
                        translateInto: baseKey,
                        translatedValue: originalKey,
                        replaceOptions: null,
                    });
                }
            }
        }
    }

    var featureConfig = {};
    if (vendorData.properties.config && vendorData.properties.config.feature) {
        featureConfig = vendorData.properties.config.feature;
    }
 
    return {
        transform: typeMapping || {},
        feature: featureConfig || {},
        mapping: mapping || []
    };
}